import {makeAutoObservable, observable} from "mobx";
import {fetchAddItem} from "@/services/cart";
import {triggerEvent, registerEvent} from "@/utils/custom-events";
import {buyboxGaProps} from "@/utils/ga-ids";

class Product {
  variantId = 0;
  quantity = 1;
  sellingPlanId = null;
  loading = false;
  // Buy-box A/B / purchase-attribution tracking (theme + GTM + GA4, no server-side).
  // `arm` = which buy box mounted (v1 old / v2 new); `frequency` = selected delivery interval.
  arm = null;
  frequency = null;
  // Config (helper-product-config) of the product currently shown in the buy box. The sticky bar
  // reads this so its title/image/price follow an in-place product swap (toggle). Stored by ref —
  // an opaque config object we only ever reassign, never mutate.
  currentProduct = null;

  constructor() {
    makeAutoObservable(this, {currentProduct: observable.ref});

    registerEvent("quantity:reset", () => {
      this.setQuantity(1);
    });
  }

  setSellingPlan(sellingPlanId) {
    this.sellingPlanId = sellingPlanId;
  }

  // Called on each buy box's mount with its arm id. Also pushes a triggerable
  // exposure event so GTM can fire the GA4 buybox_view (arm carrier + A/B denominator).
  setArm(arm) {
    this.arm = arm;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({event: "buybox_view", buybox_arm: arm});
  }

  setFrequency(frequency) {
    this.frequency = frequency;
  }

  setCurrentProduct(currentProduct) {
    this.currentProduct = currentProduct;
  }

  setVariantId(variantId) {
    const oldVariantId = this.variantId;

    if (oldVariantId !== variantId) {
      this.variantId = variantId;
      this.quantity = 1;
      this.sellingPlanId = null;

      triggerEvent("variant:change", {
        variantId: this.variantId,
      });
    }
  }

  setQuantity(quantity) {
    if (quantity <= 0) return;
    this.quantity = quantity;
  }

  async addToCart() {
    if (this.loading) return;

    try {
      const result = await fetchAddItem({
        id: this.variantId,
        quantity: this.quantity,
        ...(this.sellingPlanId && {
          selling_plan: this.sellingPlanId,
        }),
        // `_`-prefixed line-item properties are hidden from the customer but persist onto
        // the order (admin/APIs/webhooks) — a durable, verifiable buy-box attribution signal.
        properties: {
          _buybox_arm: this.arm || "unknown",
          _buybox_surface: "inline",
          _buybox_purchase_type: this.sellingPlanId ? "subscription" : "one_time",
          _buybox_selected_quantity: String(this.quantity ?? ""),
          _buybox_selected_frequency: this.sellingPlanId ? String(this.frequency ?? "") : "none",
          // GA4 client_id + session_id so the order webhook can rejoin the
          // purchase to the same GA4 user/session as the buy-box clicks.
          ...buyboxGaProps(),
        },
      });
      this.loading = false;
      return result;
    } catch (e) {
      console.error(e.message);
      this.loading = false;
    } finally {
      triggerEvent("cart:modal:toggle", {
        open: true,
      });
    }
  }
}

export const product = new Product();
